import { Entity } from '@yimoka/store';

import { tenantApiExecutor, tenantHttpGet, tenantHttpPost } from '@/http';

import type {
  ICourseEvalInviteActionInput,
  IIMUserIdentity,
  IIMService,
  IMyStaffIMTokenReply,
} from './im-service.d';

const OP_IM_TOKEN = 'my-staff-im-token';
const OP_HANDLE_INVITE = 'handle-course-eval-invite';
const OPENIM_WEB_PLATFORM_ID = 5;

/** getMyStaffIMToken 通过当前租户员工凭证换取短期 OpenIM 登录凭证。 */
export const getMyStaffIMToken = () => (
  tenantHttpGet<IMyStaffIMTokenReply, { platformID: number }>(
    '/base/rtc/manage/my/getIMToken',
    { params: { platformID: OPENIM_WEB_PLATFORM_ID } },
  )
);

/** getIMUsersByIDs 按 RTC 本地 IMUser 主键批量读取员工映射。 */
export const getIMUsersByIDs = (ids: string[]) => tenantHttpGet<
  IIMUserIdentity[],
  { ids: string[] }
>(
  '/base/rtc/manage/imUser/multi',
  { params: { ids } },
);

/** handleCourseEvalInvite 提交评课邀请的接受或拒绝状态转换。 */
export async function handleCourseEvalInvite(
  input: ICourseEvalInviteActionInput,
) {
  return await tenantHttpPost<boolean, ICourseEvalInviteActionInput>(
    '/edu/evaluation/manage/courseEvalInvite/my/handle',
    input,
  );
}

/** IMServiceEntity 用 Store 承载 OpenIM token 与评课邀请动作的请求事实。 */
export class IMServiceEntity extends Entity<IIMService> {
  /** constructor 固定租户请求执行器，所有动作自动携带当前员工与租户凭证。 */
  constructor() {
    super({
      apiExecutor: tenantApiExecutor,
      idKey: 'id',
      name: 'IMService',
    });
  }

  /** getTokenStore 返回当前员工 OpenIM token 的原子操作 Store。 */
  getTokenStore() {
    return this.getOperation<Record<string, never>, IMyStaffIMTokenReply>({
      api: getMyStaffIMToken,
      defaultValues: {},
      opType: OP_IM_TOKEN,
    });
  }

  /** getHandleInviteStore 按消息隔离评课邀请动作，避免并行卡片共享 values。 */
  getHandleInviteStore(messageID: string) {
    return this.getOperation<ICourseEvalInviteActionInput, boolean>({
      api: handleCourseEvalInvite,
      defaultValues: {
        action: 'accepted',
        conversationID: '',
        courseEvalInviteID: '',
        courseInstanceID: '',
        messageID: '',
        receiver: '',
        sender: '',
      },
      instanceKey: messageID,
      opType: OP_HANDLE_INVITE,
    });
  }

  /** disposeHandleInviteStore 精确释放已终态的消息动作 Store。 */
  disposeHandleInviteStore(messageID: string) {
    this.disposeOperation(OP_HANDLE_INVITE, messageID);
  }

  /** disposeTokenStore 释放当前员工 token 请求事实。 */
  disposeTokenStore() {
    this.disposeOperation(OP_IM_TOKEN);
  }
}

export const imServiceEntity = new IMServiceEntity();
